import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import baseURL from "../../../Url/NodeBaseURL";

const OrderTable = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const [dropdownData, setDropdownData] = useState({});
  const rowsPerPage = 7;

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await fetch(`${baseURL}/get-unique-order-details`);
        if (!response.ok) throw new Error("Failed to fetch orders");
        const data = await response.json();

        // Fetch order details for each order to get accurate netAmount from items
        const enrichedPromises = data
          .filter(
            (item) =>
              (item.transaction_status === "Orders" ||
                item.transaction_status === "ConvertedInvoice") &&
              (!item.invoice_number || item.invoice_number.trim() === "")
          )
          .map(async (item) => {
            const paid_amt = Number(item.paid_amt || 0);
            const receipts_amt = Number(item.receipts_amt || 0);
            const bal_amt = Number(item.bal_amt || 0);
            const bal_after = Number(item.bal_after_receipts || 0);
            const finalBal =
              bal_amt === receipts_amt
                ? bal_after.toFixed(2)
                : (bal_after || bal_amt).toFixed(2);
            
            // Use date if available, otherwise fallback to created_at
            const orderDate = item.date || item.created_at || null;
            
            // Fetch order details to get accurate netAmount from items (same as OrderForm)
            let netAmount = Number(item.net_amount || 0);
            let schemeAmount = Number(item.scheme_amt || 0);
            let oldItemsAmount = 0; // Always start with 0 - will be set from fetched old items (OrderForm always fetches, never uses old_exchange_amt)
            // NOTE: Do NOT use sale_return_amt from unique order details list
            // OrderForm calculates salesAmountToPass from selected sales return items (invoiceDetails)
            // When loading an existing order, if no items are selected, salesAmountToPass is 0
            // We can't know which items are selected from the dashboard, so we use 0 to match OrderForm's default behavior
            let salesAmountToPass = 0;
            let advanceAmountFromUnique = item.advance_amt;
            
            try {
              const orderDetailsResponse = await axios.get(
                `${baseURL}/get-order-details/${item.order_number}`
              );
              const uniqueData = orderDetailsResponse.data?.uniqueData || {};
              const repeatedData = orderDetailsResponse.data?.repeatedData || [];
              
              // Use values from uniqueData if available (more accurate than unique order details list)
              if (uniqueData.scheme_amt !== null && uniqueData.scheme_amt !== undefined) {
                schemeAmount = Number(uniqueData.scheme_amt || 0);
              }
              // NOTE: Do NOT use old_exchange_amt from uniqueData - OrderForm ALWAYS fetches fresh old items
              // and uses those, never the old_exchange_amt field. We'll fetch old items below.
              // NOTE: Do NOT use sale_return_amt from uniqueData - OrderForm calculates salesAmountToPass
              // from selected sales return items (invoiceDetails), not from sale_return_amt field.
              // When loading an existing order, if no items are selected, salesAmountToPass is 0.
              // We keep salesAmountToPass as 0 to match OrderForm's default behavior.
              if (uniqueData.advance_amount) {
                advanceAmountFromUnique = uniqueData.advance_amount;
              }
              
              // Filter to only "Orders" status items (same as OrderForm does)
              const filteredData = repeatedData.filter(
                (orderItem) => orderItem.transaction_status === "Orders"
              );
              // Calculate netAmount the same way OrderForm does (recalculate from components)
              // This matches the forward calculation in OrderForm (lines 1964-2001)
              if (filteredData.length > 0) {
                let calculatedNetAmount = 0;
                filteredData.forEach((orderItem) => {
                  const pricing = orderItem.pricing;
                  const itemDiscount = parseFloat(orderItem.disscount || 0);
                  const itemFestivalDiscount = parseFloat(orderItem.festival_discount || 0);
                  const itemTaxPercent = parseFloat(orderItem.tax_percent || 0);
                  
                  let itemTotal = 0;
                  if (pricing === "By Weight") {
                    const stonePrice = parseFloat(orderItem.stone_price || 0);
                    const makingCharges = parseFloat(orderItem.making_charges || 0);
                    const rateAmt = parseFloat(orderItem.rate_amt || 0);
                    const hmCharges = parseFloat(orderItem.hm_charges || 0);
                    itemTotal = stonePrice + makingCharges + rateAmt + hmCharges;
                  } else {
                    const pieceCost = parseFloat(orderItem.pieace_cost || 0);
                    const qty = parseFloat(orderItem.qty || 0);
                    itemTotal = pieceCost * qty;
                  }
                  
                  const totalDiscount = itemDiscount + itemFestivalDiscount;
                  const itemTaxable = itemTotal - totalDiscount;
                  // Match OrderForm precision (line 724 uses toFixed(10))
                  const taxAmt = parseFloat(((itemTaxable * itemTaxPercent) / 100).toFixed(10));
                  const itemNet = parseFloat((itemTaxable + taxAmt).toFixed(10));
                  
                  calculatedNetAmount += itemNet;
                });
                netAmount = calculatedNetAmount;
              }
            } catch (error) {
              console.error(`Error fetching order details for ${item.order_number}:`, error);
              // Fallback to values from unique order details if fetch fails
            }
            
            // Fetch old items to get accurate oldItemsAmount (same as OrderForm does)
            // OrderForm logic: when loading order, it ALWAYS uses oldSalesData from /get/olditems API
            // It does NOT use old_exchange_amt from uniqueData - it always fetches fresh old items
            // So we should always fetch old items and use that, matching OrderForm behavior exactly
            try {
              const oldItemsResponse = await axios.get(
                `${baseURL}/get/olditems/${item.order_number}`
              );
              if (oldItemsResponse.data && Array.isArray(oldItemsResponse.data)) {
                // Calculate oldItemsAmount from fetched old items (same as OrderForm line 2037-2040)
                // OrderForm always uses fetched old items, not old_exchange_amt from uniqueData
                const calculatedOldItemsAmount = oldItemsResponse.data.reduce(
                  (sum, oldItem) => sum + parseFloat(oldItem.total_amount || 0),
                  0
                );
                oldItemsAmount = calculatedOldItemsAmount;
              }
            } catch (error) {
              // If fetch fails, oldItemsAmount remains 0 (matching OrderForm behavior when no old items)
              console.error(`Error fetching old items for ${item.order_number}:`, error);
            }
            
            // Parse advance_amt JSON array and sum amounts (use from uniqueData if available)
            let totalAdvanceAmount = 0;
            try {
              const advanceData = advanceAmountFromUnique || item.advance_amt;
              if (advanceData) {
                const advanceEntries = typeof advanceData === 'string' 
                  ? JSON.parse(advanceData) 
                  : advanceData;
                if (Array.isArray(advanceEntries)) {
                  totalAdvanceAmount = advanceEntries.reduce(
                    (sum, entry) => sum + Number(entry.amount || 0),
                    0
                  );
                }
              }
            } catch (error) {
              console.error("Error parsing advance_amt:", error);
            }
            
            // Calculate net payable: netAmount - (schemeAmount + oldItemsAmount + salesAmountToPass + totalAdvanceAmount)
            const calculatedNetPayable = netAmount - (schemeAmount + oldItemsAmount + salesAmountToPass + totalAdvanceAmount);
            const netPayableAmount = Math.round(calculatedNetPayable);
            
            // Ensure calculatedNetPayableAmount is always a valid number
            const finalNetPayableAmount = (isNaN(netPayableAmount) || netPayableAmount === undefined || netPayableAmount === null) 
              ? (item.net_bill_amount || item.net_amount || 0)
              : netPayableAmount;
            
            // Debug logging for ORD008 (or any order with issues)
            if (item.order_number === 'ORD008' || item.order_number === 'ORD006') {
              console.log(`[OrderTable] ${item.order_number} calculation:`);
              console.log(`  netAmount: ${netAmount}`);
              console.log(`  oldItemsAmount: ${oldItemsAmount}`);
              console.log(`  deductions: ${schemeAmount + oldItemsAmount + salesAmountToPass + totalAdvanceAmount}`);
              console.log(`  calculatedNetPayable: ${calculatedNetPayable}`);
              console.log(`  netPayableAmount (rounded): ${netPayableAmount}`);
              console.log(`  fallback net_bill_amount: ${item.net_bill_amount}`);
              console.log(`  finalNetPayableAmount: ${finalNetPayableAmount}`);
            }
            
            return { 
              ...item, 
              finalBal: Number(finalBal), 
              date: orderDate,
              calculatedNetPayableAmount: finalNetPayableAmount
            };
          });

        const enriched = await Promise.all(enrichedPromises);
        const filtered = enriched.filter((item) => item.finalBal > 0);

        setOrders(filtered);
      } catch (err) {
        console.error(err);
        setError("Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const totalPages = Math.ceil(orders.length / rowsPerPage);

  const handlePrev = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const handleNext = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);

  const paginatedOrders = orders.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
    const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) return "";
    return `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1
    ).padStart(2, "0")}-${date.getFullYear()}`;
    } catch (error) {
      console.error("Error formatting date:", error, dateString);
      return "";
    }
  };

  const calculateDelayDates = (invoiceDate) => {
    if (!invoiceDate) return "-";
    const today = new Date();
    const date = new Date(invoiceDate);
    const diffTime = today - date;
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const handleCheckboxChange = async (order_number) => {
    if (expandedOrder === order_number) {
      setExpandedOrder(null);
      return;
    }

    setExpandedOrder(order_number);

    if (!dropdownData[order_number]) {
      try {
        const response = await axios.get(
          `${baseURL}/get-order-details/${order_number}`
        );

        const filtered = response.data.repeatedData.filter(
          (item) => item.invoice_number === null
        );

        setDropdownData((prev) => ({
          ...prev,
          [order_number]: filtered,
        }));
      } catch (error) {
        console.error("Error fetching order details:", error);
        alert("Error fetching order details. Please try again.");
      }
    }
  };

  const handleOrderClick = async (order_number) => {
    try {
      const response = await axios.get(
        `${baseURL}/get-order-details/${order_number}`
      );

      const filteredData = response.data.repeatedData.filter(
        (item) => item.transaction_status === "Orders"
      );
      // Pass all order items, not just the first one
      const orderDetails = filteredData || [];
      
      // Get the date from uniqueData if available
      const orderDate = response.data.uniqueData?.date 
        ? new Date(response.data.uniqueData.date).toLocaleDateString("en-CA", {
            timeZone: "Asia/Kolkata",
          })
        : null;

      navigate("/orders", {
        state: {
          orderDetails,
          order_number: order_number,
          date: orderDate,
        },
      });

      localStorage.setItem("orderDetails", JSON.stringify(orderDetails));
    } catch (error) {
      console.error("Error fetching order details:", error);
      alert("Error fetching order details. Please try again.");
    }
  };

  if (loading)
    return <div style={{ padding: 10, textAlign: "center" }}>Loading...</div>;
  if (error)
    return (
      <div style={{ padding: 10, color: "red", textAlign: "center" }}>
        {error}
      </div>
    );

  return (
    <div style={{ width: "100%", marginTop: "8px" }}>
      <div
        style={{
          maxHeight: "400px",
          height: "240px",
          overflowY: "auto",
          width: "100%",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "13px",
          }}
        >
          <thead>
            <tr
              style={{ backgroundColor: "#f5f5f5", position: "sticky", top: 0 }}
            >
              <th style={thStyle}></th>
              <th style={thStyle}>SI</th>
              <th style={thStyle}>Date</th>
              <th style={thStyle}>Account</th>
              <th style={thStyle}>Order No.</th>
              <th style={thStyle}>Amount</th>
              <th style={thStyle}>Delayed (days)</th>
            </tr>
          </thead>
          <tbody>
            {paginatedOrders.map((order, index) => (
              <React.Fragment key={index}>
                <tr
                  style={{
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                    transition: "background-color 0.2s",
                  }}
                >
                  <td style={tdStyle}>
                    <input
                      type="checkbox"
                      checked={expandedOrder === order.order_number}
                      onChange={() => handleCheckboxChange(order.order_number)}
                    />
                  </td>
                  <td style={tdStyle}>
                    {(currentPage - 1) * rowsPerPage + index + 1}
                  </td>
                  <td
                    style={tdStyle}
                    onClick={() => handleOrderClick(order.order_number)}
                  >
                    {formatDate(order.date)}
                  </td>
                  <td
                    style={tdStyle}
                    onClick={() => handleOrderClick(order.order_number)}
                  >
                    {order.account_name}
                  </td>
                  <td
                    style={tdStyle}
                    onClick={() => handleOrderClick(order.order_number)}
                  >
                    {order.order_number}
                  </td>
                  <td
                    style={tdStyle}
                    onClick={() => handleOrderClick(order.order_number)}
                  >
                    {order.calculatedNetPayableAmount !== undefined 
                      ? order.calculatedNetPayableAmount 
                      : (order.net_bill_amount || order.net_amount || 0)}
                  </td>
                  <td style={tdStyle}>{calculateDelayDates(order.date)}</td>
                </tr>
                {expandedOrder === order.order_number && (
                  <tr>
                    <td colSpan={6} style={{ padding: "0px 10px" }}>
                      <table
                        style={{
                          width: "100%",
                          fontSize: "12px",
                          backgroundColor: "#f9f9f9",
                          marginTop: "5px",
                          borderCollapse: "collapse",
                        }}
                      >
                        <thead>
                          <tr style={{ borderBottom: "1px solid #ccc" }}>
                            <th style={subTh}>Category</th>
                            <th style={subTh}>Sub Category</th>
                            <th style={subTh}>Vendor Name</th>
                            <th style={subTh}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {!dropdownData[order.order_number] ? (
                            <tr>
                              <td colSpan={6} style={subTd}>
                                Loading details...
                              </td>
                            </tr>
                          ) : dropdownData[order.order_number].length === 0 ? (
                            <tr>
                              <td colSpan={6} style={subTd}>
                                No items with null invoice number.
                              </td>
                            </tr>
                          ) : (
                            dropdownData[order.order_number].map((item, i) => {
                              let status = "Not Assigned";
                              if (item.vendor_name) {
                                if (item.submission_status === "in review")
                                  status = "Under Review";
                                else if (item.submission_status === "selected")
                                  status = "Ready for Delivery";
                                else if (!item.submission_status)
                                  status = "With Vendor";
                              }
                              return (
                                <tr
                                  key={i}
                                  style={{
                                    transition: "background-color 0.2s",
                                    borderBottom: "1px solid #eee",
                                  }}
                                >
                                  <td style={subTd}>{item.category}</td>
                                  <td style={subTd}>{item.product_name}</td>
                                  <td style={subTd}>{item.vendor_name}</td>
                                  <td style={subTd}>{status}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div style={{ marginTop: "10px", textAlign: "center" }}>
        <button
          onClick={handlePrev}
          disabled={currentPage === 1}
          style={buttonStyle}
        >
          &laquo;
        </button>
        <span style={{ margin: "0 10px", fontSize: "12px" }}>
          Page {currentPage} of {totalPages}
        </span>
        <button
          onClick={handleNext}
          disabled={currentPage === totalPages}
          style={buttonStyle}
        >
          &raquo;
        </button>
      </div>
    </div>
  );
};

// Styles
const thStyle = {
  padding: "6px 10px",
  textAlign: "left",
  borderBottom: "1px solid #ddd",
  fontWeight: "600",
  color: "#b77318",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "6px 10px",
  textAlign: "left",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  fontWeight: "400",
};

const subTh = {
  textAlign: "left",
  padding: "6px",
  borderBottom: "1px solid #ccc",
  color: "white",
};

const subTd = {
  padding: "6px 10px",
  textAlign: "left",
  fontWeight: "400",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const buttonStyle = {
  padding: "5px 12px",
  fontSize: "13px",
  cursor: "pointer",
};

export default OrderTable;
